import dynamic from 'next/dynamic';
import { Stack, Grid } from '@global-ecom/nitro-uds/elements';

import { useProductCustomization } from 'hooks/useProductCustomization';
import { useTranslate } from 'hooks/useTranslations';
import { useSticky } from 'hooks/useSticky';
import { MyCustomizer } from 'ui/components/ProductCustomizers';
import {
  AddToCartErrorMessage,
  ProductActions,
  ProductBreadcrumb,
  ProductImageGallery,
  ProductInfo,
  ProductVariantSelectors,
} from 'ui/pages/ProductDetailsPages';
import { SanityContentSlot } from 'ui/components/SanityContentSlot';
import ScheduledArrivalDate from 'ui/components/ScheduledArrivalDate';
import { MixedCartErrorMessage } from 'ui/components/MixedCartErrorMessage';

import { PdpProps } from './ProductDetailsPage';

const PincodeServiceabilityCheck = dynamic(
  () => import('ui/components/PincodeServiceabilityCheck'),
  { ssr: false }
);

export const PdpSimplifiedLayout = ({
  masterProduct,
  variation,
  content,
  mixedCartNotAllowed,
  sizeHasChanged,
}: PdpProps) => {
  const t = useTranslate();
  const { stickyRef, stickyTop } = useSticky(3);
  const { customizationReceived, isCustomizingProduct } =
    useProductCustomization();

  const isMasterSoldOutWithRecommender =
    masterProduct?.displayOutOfStock?.soldoutWithRecommender;

  return (
    <>
      <ProductBreadcrumb />
      <Stack asChild gap={{ _: 'sm', md: 'lg' }}>
        <section
          aria-labelledby="pdp-product-title"
          className="mobile:mt-4 tablet:mt-6 desktop:mt-8"
        >
          {variation && isCustomizingProduct ? (
            <MyCustomizer
              variation={variation}
              onProductCustomized={customizationReceived}
            />
          ) : (
            <Grid columns={{ _: '1', md: '8', lg: '12' }}>
              <ProductImageGallery className="grid-cols-1" />
              <section
                aria-label={t('productInfo')}
                className="order-1 h-full md:col-span-4"
              >
                <Stack
                  ref={stickyRef}
                  gap="base"
                  className={`md:pt-3 md:sticky md:top-[${stickyTop}px]`}
                >
                  <ProductInfo />
                  <ProductVariantSelectors />
                  {!isMasterSoldOutWithRecommender && (
                    <>
                      <AddToCartErrorMessage />
                      {sizeHasChanged && (
                        <MixedCartErrorMessage
                          mixedCartNotAllowed={mixedCartNotAllowed!}
                        />
                      )}
                      {variation?.displayOutOfStock?.presale &&
                        variation?.displayOutOfStock?.validTo && (
                          <ScheduledArrivalDate
                            preOrderDeliveryDate={
                              variation.displayOutOfStock.validTo
                            }
                          />
                        )}
                      <ProductActions
                        mixedCartNotAllowed={mixedCartNotAllowed}
                      />
                      <PincodeServiceabilityCheck />
                    </>
                  )}
                </Stack>
              </section>
            </Grid>
          )}
        </section>
      </Stack>
      <SanityContentSlot
        items={content?.belowProductGoogleShopping}
        slotId="belowProductGoogleShopping"
        documentId={masterProduct?.id}
      />
    </>
  );
};
